import { Formik } from 'formik';
import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { ActionButton, Buttons, CancelButton, Form, FormMessage, Icon, PromptNavigation } from '~/components';
import { useApi, useWorkspace } from '~/contexts';
import { useForm } from '~/hooks';
import { PageLoader } from '~/routes/public/pages';
import { colors } from '~/styles';
import SettingsActionsBanner from '../SettingsActionsBanner';
import SettingsFormSection from '../SettingsFormSection';
import MicrosoftTeamsChannelSelect from './MicrosoftTeamsChannelSelect';

const Warning = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1rem;
  color: ${colors.grey75};
`;

export default function MicrosoftTeamsChannelForm({ project }) {
  const api = useApi();
  const { workspace } = useWorkspace();
  const [{ status, message, isSubmitting, saved }, form] = useForm();
  const [channel, setChannel] = useState();
  const [loadFailed, setLoadFailed] = useState(false);

  const microsoftTeams = useCallback(
    () => api.www.workspaces(workspace.id).integrations().microsoft.teams,
    [api, workspace.id],
  );

  const fetchData = useCallback(async () => {
    try {
      const { data } = await microsoftTeams().getProjectChannels();
      setChannel(data.find((row) => row.projectId === project.id) ?? null);
      setLoadFailed(false);
    } catch {
      setChannel(null);
      setLoadFailed(true);
    }
  }, [microsoftTeams, project.id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (channel === undefined) return <PageLoader />;

  const handleSubmit = async (values, formik) => {
    try {
      form.submit();

      if (values.channel) {
        await microsoftTeams().setProjectChannel(project.id, {
          teamId: values.channel.teamId,
          channelId: values.channel.channelId,
          webUrl: values.channel.webUrl ?? null,
        });
      } else if (channel) {
        await microsoftTeams().removeProjectChannel(project.id);
      }

      await fetchData();
      form.save();
      formik.resetForm({ values });
    } catch ({ message }) {
      form.error({ message });
    }
  };

  return (
    <Formik enableReinitialize initialValues={{ channel }} onSubmit={handleSubmit}>
      {(formik) => (
        <Form>
          <SettingsFormSection title="Microsoft Teams">
            {loadFailed ? (
              <Warning>
                <Icon icon="exclamation-triangle" color={colors.warning} />
                This project&apos;s Microsoft Teams channel could not be loaded.
              </Warning>
            ) : (
              <>
                {channel?.lastErrorMessage && (
                  <Warning>
                    <Icon icon="exclamation-triangle" color={colors.warning} />
                    {channel.lastErrorMessage}
                  </Warning>
                )}

                <Form.Control help="Assign a project channel to get project updates in Microsoft Teams.">
                  <MicrosoftTeamsChannelSelect name="channel" />
                </Form.Control>
              </>
            )}
          </SettingsFormSection>

          <SettingsActionsBanner>
            {status && <FormMessage.Error spaceRight>{message || 'An error has occurred.'}</FormMessage.Error>}

            {!loadFailed && project.permissions.edit && (
              <Buttons>
                <CancelButton disabled={!formik.dirty} onClick={() => formik.resetForm()}>
                  Cancel
                </CancelButton>

                <ActionButton isLoading={isSubmitting} ok={saved} onClick={formik.submitForm}>
                  Save
                </ActionButton>
              </Buttons>
            )}
          </SettingsActionsBanner>

          <PromptNavigation when={formik.dirty} />
        </Form>
      )}
    </Formik>
  );
}
